import { colorsLeague } from "../../utils/dataColors";
import { IColor } from "../sections/GridSections/IGridSections";
import { ILink, ILeague, ISection } from "./contextVacancies/ContextVacancies";
import {
  Bundle,
  Link,
  Group,
  Section,
  ValidationErrorReason,
  InvalidLinkChanges,
  InvalidSectionChanges,
  InvalidSessionChanges,
  InvalidGroupChanges,
} from "../../models/ISchema";
import i18n from "@config/i18n";

/** Synthetic id used for the "without instructor assigned" row in the instructor table. */
export const UNASSIGNMENT_ID = "unassignment";
export const isUnassignment = (id?: string): boolean => id === UNASSIGNMENT_ID;

type Error = "warning" | "error";

export interface IObjValidationError {
  type: Error;
  text: string;
  title?: string;
  reason?: ValidationErrorReason;
  leagueCode?: string;
}
export const assigmentColor = (idColor: number) => {
  const color = colorsLeague.find((item: IColor) => item.id === idColor);
  return color;
};

export const linksBundle = (leagues: Array<ILeague>) => {
  const newLeagues: Array<ILeague> = [];
  leagues.forEach(league => {
    const newLeague: ILeague = league;
    const newSections: Array<ISection> = [];
    league.sections.forEach(section => {
      const newSection: ISection = section;
      const newBundle: Array<ILink> = [];
      section.link.forEach((bundle: any) => {
        const filterLeagues = leagues.find(item => item.code === bundle.code);
        const objLinkBundle = {
          code: bundle.code,
          codeName: filterLeagues.codeName,
          color: filterLeagues.color,
          idColor: filterLeagues.idColor,
          vacancies: bundle.vacancies,
        };
        newBundle.push(objLinkBundle);
      });
      newSection.link = newBundle;
      newSections.push(section);
    });
    newLeague.sections = newSections;
    newLeagues.push(newLeague);
  });
  return newLeagues;
};

export const dataLeagues = (base: Bundle) => {
  const leagues = [];
  base.links.forEach((link: Link, indexLeague: number) => {
    const assigmentLeague = assigmentColor(indexLeague);
    const groups = [];
    const sections = [];
    const linkCampus = link?.course?.curriculum?.program?.campus?.code ?? "";

    link.groups.forEach((group: Group) => {
      const objGroup = {
        id: group.id || "-",
        vacancies: group?.capacity || 0,
        isActive: Boolean(group?.isActive),
        label: group.label || "-",
        enrollment: group?.enrollmentStats?.usedCapacity || 0,
        waitList: group?.enrollmentStats?.waitingListsCount || 0,
        curriculum: group.curriculum ? group.curriculum.code : "-",
        course: group.course ? group.course : {},
        campus: linkCampus,
        program: group.course ? group.course.curriculum.program.code : "-",
        clientCode: group?.clientCode,
        openGroup: group?.visibleForEnrollment,
        groupSections: group?.groupSections || [],
        term: group?.term,
        isReferent: group?.isReferent,
        groupCapacity: group?.groupCapacity,
        enrollmentStats: group?.enrollmentStats,
      };
      groups.push(objGroup);
    });

    link.sections.forEach((section: Section) => {
      const sectionBundle = [];
      section.linksByBundles.forEach((bundle: Link) => {
        if (bundle.code !== link.code) {
          sectionBundle.push(bundle);
        }
      });

      const objSection = {
        typeComponent: section.component.code,
        sectionId: section.id,
        componentId: section.component.id,
        vacancies: section.vacancies,
        link: sectionBundle,
      };
      sections.push(objSection);
    });

    const objLeague = {
      code: link.code,
      codeName: assigmentLeague.codeName,
      color: assigmentLeague.color,
      course: link?.course,
      groups: groups,
      id: base.id,
      idColor: indexLeague,
      label: base.label,
      clientCode: link?.label,
      sections: sections,
      linkId: link.id,
      instructors: link?.instructorResponsability,
      totalBlocks: link?.totalBlocks,
      blocksWithoutInstructorAssigment: link?.blocksWithoutInstructorAssigment,
      bundle: link?.bundle,
    };

    leagues.push(objLeague);
  });

  const bundleLinks = linksBundle(leagues);
  return bundleLinks;
};

const getErrorTitle = (reason: ValidationErrorReason) => {
  switch (reason) {
    case "EXEEDS_MAX":
      return i18n.t("bundle.create-group.errors.EXEEDS_MAX.title");
    case "ENROLLMENTS_NOT_COVERED":
      return i18n.t("bundle.create-group.errors.ENROLLMENTS_NOT_COVERED.title");
    case "GROUP_VACANCIES_EXTERNAL_VALIDATION":
      return i18n.t("bundle.create-group.errors.GROUP_VACANCIES_EXTERNAL_VALIDATION.title");
    case "LOWER_THAN_MIN":
      return i18n.t("bundle.create-group.errors.LOWER_THAN_MIN.title");
    case "CLASSROOM_CAPACITY_EXCEEDED":
      return i18n.t("bundle.create-group.errors.CLASSROOM_CAPACITY_EXCEEDED.title");
    case "GROUP_CODE_ALREADY_TAKEN":
      return i18n.t("bundle.create-group.errors.GROUP_CODE_ALREADY_TAKEN.title");
    case "SUBJECT_NOT_FOUND":
      return i18n.t("bundle.create-group.errors.SUBJECT_NOT_FOUND.title");
    default:
      return "";
  }
};

export const getValidationErrorReason = (
  code: ValidationErrorReason,
  section?: string,
  objValue?: any,
) => {
  const sectionMs =
    section && section !== ""
      ? `${i18n.t("bundle.create-group.errors.section-key")} ${section}.`
      : "";
  const standardMs =
    objValue && objValue.standard
      ? `(${i18n.t("bundle.create-group.errors.standard-key")}: ${objValue.standard}).`
      : "";
  const enrrollmentMs =
    objValue && objValue.enrrollment
      ? `(${i18n.t("bundle.create-group.errors.enrollments-key")}: ${objValue.enrrollment})`
      : "";
  const capacityMs =
    objValue && objValue.capacity
      ? `(${i18n.t("bundle.create-group.errors.capacity-key")}: ${objValue.capacity}).`
      : "";
  const sessionMs =
    objValue && objValue.sessionId
      ? `${i18n.t("bundle.create-group.errors.session-key")} ${objValue.sessionId}.`
      : "";
  const groupMs =
    objValue && objValue.groupLabel
      ? `${i18n.t("bundle.create-group.errors.group-key")} ${objValue.groupLabel}.`
      : "";
  const leagueCode = objValue && objValue.leagueCode ? objValue.leagueCode : null;

  let objError: IObjValidationError = {
    type: null,
    text: null,
    title: getErrorTitle(code),
    reason: code,
    leagueCode: leagueCode,
  };

  switch (code) {
    //SECTION -> warning
    case "EXEEDS_MAX":
      objError = {
        ...objError,
        type: "warning",
        text: i18n.t("bundle.create-group.errors.EXEEDS_MAX.message", {
          sectionMs,
          standardMs,
        }),
      };
      break;
    //GROUP -> error
    case "ENROLLMENTS_NOT_COVERED":
      objError = {
        ...objError,
        type: "error",
        text: i18n.t("bundle.create-group.errors.ENROLLMENTS_NOT_COVERED.message", {
          sectionMs,
          groupMs,
          enrrollmentMs,
        }),
      };
      break;
    case "GROUP_VACANCIES_EXTERNAL_VALIDATION":
      objError = {
        ...objError,
        type: "error",
        text: i18n.t("bundle.create-group.errors.GROUP_VACANCIES_EXTERNAL_VALIDATION.message"),
      };
      break;
    //SECTION -> warning
    case "LOWER_THAN_MIN":
      objError = {
        ...objError,
        type: "warning",
        text: i18n.t("bundle.create-group.errors.LOWER_THAN_MIN.message", {
          sectionMs,
        }),
      };
      break;
    //SESSION -> warning
    case "CLASSROOM_CAPACITY_EXCEEDED":
      objError = {
        ...objError,
        type: "warning",
        text: i18n.t("bundle.create-group.errors.CLASSROOM_CAPACITY_EXCEEDED.message", {
          sectionMs,
          sessionMs,
          capacityMs,
        }),
      };
      break;
    case "GROUP_CODE_ALREADY_TAKEN":
      objError = {
        ...objError,
        type: "error",
        text: i18n.t("bundle.create-group.errors.GROUP_CODE_ALREADY_TAKEN.message"),
      };
      break;
    case "SUBJECT_NOT_FOUND":
      objError = {
        ...objError,
        type: "error",
        text: i18n.t("bundle.create-group.errors.SUBJECT_NOT_FOUND.message"),
      };
      break;
  }
  return objError;
};

export const linkValidation = (data: InvalidLinkChanges) => {
  const errorLinks = [];
  // sectionChanges
  data.sectionChanges &&
    data.sectionChanges.forEach((section: InvalidSectionChanges) => {
      section.validationErrors &&
        section.validationErrors.forEach(errorSection => {
          if (errorSection && errorSection.__typename === "InvalidComponentSize") {
            const standardObj = {
              standard:
                errorSection.component && errorSection.component.academicStandard
                  ? errorSection.component.academicStandard
                  : section?.section?.courseComponent &&
                      section?.section?.courseComponent?.academicStandard
                    ? section?.section?.courseComponent?.academicStandard
                    : "",
              leagueCode: data.link && data.link.code ? data.link.code : null,
            };
            const errorMs = getValidationErrorReason(
              errorSection.reason,
              section.section.id,
              standardObj,
            );
            errorMs && errorLinks.push(errorMs);
          }
        });
    });
  // sessionChanges
  data.sessionChanges &&
    data.sessionChanges.forEach((session: InvalidSessionChanges) => {
      session.validationErrors &&
        session.validationErrors.forEach(errorSession => {
          if (errorSession) {
            const capacityObj = {
              capacity:
                errorSession.classroom && errorSession.classroom.capacity
                  ? errorSession.classroom.capacity
                  : "",
              sessionId: session.session.id,
              leagueCode: data.link && data.link.code ? data.link.code : null,
            };
            const errorMs = getValidationErrorReason(
              errorSession.reason,
              session.session.section.id,
              capacityObj,
            );
            errorMs && errorLinks.push(errorMs);
          }
        });
    });
  // groupChanges
  data.groupChanges &&
    data.groupChanges.forEach((group: InvalidGroupChanges) => {
      group.validationErrors.forEach(errorGroup => {
        if (errorGroup.__typename === "InvalidFieldValidation") {
          const errorMs = getValidationErrorReason(errorGroup.reason);
          errorMs && errorLinks.push(errorMs);
        } else if (errorGroup.__typename === "ExternalGroupValidation") {
          const groupObj = {
            groupLabel: group.group && group.group.code ? group.group.code : "",
            leagueCode: data.link && data.link.code ? data.link.code : null,
          };
          const errorMs = getValidationErrorReason(errorGroup.reason, "", groupObj);
          errorMs && errorLinks.push(errorMs);
        } else if (errorGroup.__typename === "InvalidGroupEnrollment") {
          const groupObj = {
            groupLabel: group.group && group.group.code ? group.group.code : "",
            leagueCode: data.link && data.link.code ? data.link.code : null,
          };
          const errorMs = getValidationErrorReason(errorGroup.reason, "", groupObj);
          errorMs && errorLinks.push(errorMs);
        }
      });
    });

  return errorLinks;
};

export const groupValidation = (data: any, link: Link) => {
  const errorLinks = [];

  data &&
    data.forEach(errorGroup => {
      if (errorGroup.__typename === "ExternalGroupValidation") {
        const groupObj = {
          groupLabel: "",
          leagueCode: link && link.code ? link.code : null,
        };

        const errorMs = getValidationErrorReason(errorGroup.reason, "", groupObj);
        errorMs && errorLinks.push(errorMs);
      }
    });

  return errorLinks;
};

export const getTotalBlocksPercent = (instructorTotalBlocks = 0, generalTotalBlocks = 0) =>
  [instructorTotalBlocks, generalTotalBlocks].includes(0)
    ? 0
    : +((instructorTotalBlocks * 100) / generalTotalBlocks).toFixed(2);

export const getGroupComponentErrorMessages = (
  component: string,
  code: string,
  existingLabels: string[] = [],
  shouldBeNumeric = true,
) => {
  const componentErrors = [];

  if (isNaN(+code) && shouldBeNumeric) {
    componentErrors.push("Ingresa números enteros de 1 a 999.");
  }

  if (code.startsWith("0") && shouldBeNumeric) {
    componentErrors.push("El número no debe ser antecedido por uno o más ceros (01, 001).");
  }

  if (existingLabels.includes(code)) {
    componentErrors.push(
      "No repetir el número de código de sección en una misma asignatura, periodo académico y sede.",
    );
  }

  if (!componentErrors?.length) return null;

  return {
    component,
    errors: componentErrors,
  };
};
